import { createForm } from "@felte/solid";
import { Loading01 } from "@liftedcare/solidjs-untitled-icons/duocolor";
import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import { Component } from "solid-js";

import gqlClient from "../../../schema/gqlAuthorizedRequest.js";
import {
  DocumentTypeType,
  GeneratePdfExportQueryQueryVariables,
} from "../../../schema/graphql.js";
import { graphql } from "../../../schema/index.js";
import { documentTypeEnumToLabel } from "../../../utils/enumToLabel.js";
import { formatDate } from "../../../utils/formatDate.js";
import { getGqlErrorMessage } from "../../../utils/getGqlErrorMessage.js";
import { Button } from "../../common/Button.js";
import DataLoadingSwitch from "../../common/DataLoadingSwitch.js";
import { Checkbox } from "../../form/Checkbox.js";
import Combobox from "../../form/Combobox.js";
import { FormContextProvider } from "../../form/FormContext.js";
import { FormLabel } from "../../form/FormLabel.js";
import { ISelectOption } from "../../form/Select.js";
import Modal, {
  IModalProps,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../modal/Modal.js";
import { useToast } from "../../toast/index.js";

const getApplicantPdfExportQuery = graphql(`
  query generatePdfExportQuery(
    $id: Int!
    $separateSectionPDFs: Boolean!
    $applicantPdfConfig: ApplicantPdfConfigArgs!
    $documentIds: [Int!]!
  ) {
    generatePdfExport(
      id: $id
      separateSectionPDFs: $separateSectionPDFs
      applicantPdfConfig: $applicantPdfConfig
      documentIds: $documentIds
    ) {
      success
    }
  }
`);

const getApplicantDocumentsForExportQuery = graphql(`
  query getApplicantDocumentsForExport($id: Int!) {
    getDocuments(applicantId: $id) {
      documents {
        id
        type
        files {
          fileName
        }
        createdAt
        updatedAt
      }
    }
  }
`);

export const ApplicantProfileExportModal: Component<
  IModalProps & { applicantId: number }
> = (props) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const getApplicantPdfExportMutation = useMutation(() => ({
    mutationFn: async (variables: GeneratePdfExportQueryQueryVariables) => {
      return gqlClient.request(getApplicantPdfExportQuery, variables);
    },
    onSuccess: () => {
      toast.success("Success", "Applicant export requested");
      props.onClose();
      queryClient.invalidateQueries({
        queryKey: ["exportRequests", "applicantId", props.applicantId],
      });
    },
    onError(error) {
      toast.error(
        "Error",
        getGqlErrorMessage(error, "Error requesting applicant export"),
      );
    },
  }));

  const formMethods = createForm({
    onSubmit: (values) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { all, separateSectionPDFs, documentIds, ...applicantPdfConfig } =
        values;
      getApplicantPdfExportMutation.mutate({
        id: props.applicantId,
        separateSectionPDFs,
        applicantPdfConfig,
        documentIds: documentIds ?? [],
      });
    },
    initialValues: {
      all: true,
      includeProfile: true,
      includeHealthDeclaration: true,
      includeLifeInTheUk: true,
      includeNameHistory: true,
      includeEmploymentHistory: true,
      includeAddressHistory: true,
      includeNationalityHistory: true,
      includeEducationHistory: true,
      includeNextOfKin: true,
      includeConvictions: true,
      includeDbs: true,
      includeReferences: true,
      includeIdvCheck: true,
      includeRtwAppearanceCheck: true,
      includeDrivingDetails: true,
      includeComments: true,
      separateSectionPDFs: false,
      documentIds: [],
    },
  });
  const form = formMethods.form;

  const documents = useQuery(() => ({
    queryKey: ["documentsForExport", props.applicantId],
    queryFn: async () =>
      gqlClient.request(getApplicantDocumentsForExportQuery, {
        id: props.applicantId,
      }),
    enabled: props.show,
  }));

  const exportableDocuments = () => {
    return (
      documents.data?.getDocuments.documents
        .filter((doc) => doc.files.length > 0)
        .map((doc) => {
          const name =
            doc.type === DocumentTypeType.Other
              ? (doc.files.at(0)?.fileName ?? "Unknown")
              : documentTypeEnumToLabel(doc.type);
          const option: ISelectOption = {
            name: `${name} - ${formatDate(doc.createdAt)}`,
            value: doc.id,
          };
          return option;
        }) ?? []
    );
  };

  return (
    <FormContextProvider formMethods={formMethods}>
      <form use:form>
        <Modal size="lg" show={props.show} onClose={() => props.onClose()}>
          <ModalHeader>Export applicant data</ModalHeader>
          <ModalBody>
            <h2 class="font-semibold">Sections</h2>
            <FormLabel>
              Please select the sections you'd like to export
            </FormLabel>
            <div class="flex gap-2">
              <FormLabel class="text-sm font-medium">Toggle all</FormLabel>

              <Checkbox
                name="all"
                onChange={(value) => {
                  formMethods.setFields({
                    all: value,
                    includeProfile: value,
                    includeHealthDeclaration: value,
                    includeLifeInTheUk: value,
                    includeNameHistory: value,
                    includeEmploymentHistory: value,
                    includeAddressHistory: value,
                    includeNationalityHistory: value,
                    includeEducationHistory: value,
                    includeNextOfKin: value,
                    includeConvictions: value,
                    includeDbs: value,
                    includeReferences: value,
                    includeIdvCheck: value,
                    includeRtwAppearanceCheck: value,
                    includeDrivingDetails: value,
                    includeComments: value,
                    separateSectionPDFs: formMethods.data(
                      "separateSectionPDFs",
                    ),
                    documentIds: formMethods.data("documentIds"),
                  });
                }}
              />
            </div>

            <section class="grid grid-cols-2 gap-x-24 gap-y-3 pr-12">
              <div class="flex justify-between">
                <FormLabel for="includeProfile">Profile</FormLabel>
                <span>
                  <Checkbox id="includeProfile" name="includeProfile" />
                </span>
              </div>
              <div class="flex justify-between">
                <FormLabel for="includeHealthDeclaration">
                  Health Declaration
                </FormLabel>

                <span>
                  <Checkbox
                    id="includeHealthDeclaration"
                    name="includeHealthDeclaration"
                  />
                </span>
              </div>
              <div class="flex justify-between">
                <FormLabel for="includeLifeInTheUk">Life in the UK</FormLabel>

                <span>
                  <Checkbox id="includeLifeInTheUk" name="includeLifeInTheUk" />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeNameHistory">Name history</FormLabel>

                <span>
                  <Checkbox id="includeNameHistory" name="includeNameHistory" />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeEmploymentHistory">
                  Employment history
                </FormLabel>

                <span>
                  <Checkbox
                    id="includeEmploymentHistory"
                    name="includeEmploymentHistory"
                  />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeAddressHistory">
                  Address history
                </FormLabel>

                <span>
                  <Checkbox
                    id="includeAddressHistory"
                    name="includeAddressHistory"
                  />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeNationalityHistory">
                  Nationality history
                </FormLabel>

                <span>
                  <Checkbox
                    id="includeNationalityHistory"
                    name="includeNationalityHistory"
                  />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeEducationHistory">
                  Education history
                </FormLabel>

                <span>
                  <Checkbox
                    id="includeEducationHistory"
                    name="includeEducationHistory"
                  />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeNextOfKin">Next of kin</FormLabel>

                <span>
                  <Checkbox id="includeNextOfKin" name="includeNextOfKin" />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeConvictions">Convictions</FormLabel>

                <span>
                  <Checkbox id="includeConvictions" name="includeConvictions" />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeDbs">DBS</FormLabel>

                <span>
                  <Checkbox id="includeDbs" name="includeDbs" />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeReferences">References</FormLabel>

                <span>
                  <Checkbox id="includeReferences" name="includeReferences" />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeIdvCheck">IDV check</FormLabel>

                <span>
                  <Checkbox id="includeIdvCheck" name="includeIdvCheck" />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeRtwAppearanceCheck">
                  RTW visual appearance check
                </FormLabel>

                <span>
                  <Checkbox
                    id="includeRtwAppearanceCheck"
                    name="includeRtwAppearanceCheck"
                  />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeDrivingDetails">
                  Driving/vehicle details
                </FormLabel>

                <span>
                  <Checkbox
                    id="includeDrivingDetails"
                    name="includeDrivingDetails"
                  />
                </span>
              </div>

              <div class="flex justify-between">
                <FormLabel for="includeComments">Comments</FormLabel>

                <span>
                  <Checkbox id="includeComments" name="includeComments" />
                </span>
              </div>
            </section>
            <div class="space-y-6 pt-4">
              <Checkbox
                id="separateSectionPDFs"
                name="separateSectionPDFs"
                label={
                  <FormLabel>Export sections as individual files</FormLabel>
                }
              />
              <DataLoadingSwitch
                loader={documents}
                loadingContent={
                  <div class="flex items-center gap-4">
                    <Loading01 class="size-8 animate-spin" /> Loading
                  </div>
                }
              >
                <Combobox
                  name={"documentIds"}
                  options={exportableDocuments()}
                  isSingle={false}
                  label={
                    <span>
                      <h2 class="font-semibold">Documents</h2>
                      <FormLabel>
                        Please select any additional document(s) you'd like to
                        include
                      </FormLabel>
                    </span>
                  }
                  maxDisplayValues={3}
                  class="w-full p-2"
                  listContainerClass="!max-h-[20svh]"
                />
              </DataLoadingSwitch>

              <p>
                The export file can take a few minutes to prepare. A download
                link will be provided by email when it is ready.
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={props.onClose}>
              Cancel
            </Button>
            <Button color="primary" onClick={formMethods.handleSubmit}>
              Prepare export
            </Button>
          </ModalFooter>
        </Modal>
      </form>
    </FormContextProvider>
  );
};
